import { useState, useEffect } from 'react';
import { useAuth } from '../../../AuthContext';
import { fetchData, postData } from '../../../apiService';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Autocomplete,
  CircularProgress,
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { Close, Add } from '@mui/icons-material';
import './ItemModal.css';

// Compact section header
const sectionStyle = {
  fontSize: '0.62rem',
  fontWeight: 700,
  color: '#1e3a8a',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  mb: 0.5,
  mt: 0.5,
  pb: 0.15,
  borderBottom: '1px solid #e5e7eb'
};

// Compact field styling
const fieldStyle = {
  '& .MuiOutlinedInput-root': {
    '& input, & .MuiSelect-select': { padding: '6px 10px', fontSize: '0.78rem' },
    minHeight: '32px'
  },
  '& .MuiInputLabel-root': { fontSize: '0.75rem' },
  '& .MuiInputLabel-shrink': { fontSize: '0.7rem' },
  '& .MuiAutocomplete-input': { padding: '6px 10px !important', fontSize: '0.78rem' },
  '& .MuiFormHelperText-root': { fontSize: '0.68rem', marginTop: '2px' },
  '& .MuiSvgIcon-root': { fontSize: '1.1rem' }
};

// Dropdown list styling
const autocompleteListStyle = {
  '& .MuiAutocomplete-listbox': {
    '& .MuiAutocomplete-option': {
      fontSize: '0.75rem',
      minHeight: '32px',
      padding: '4px 10px'
    }
  },
  '& .MuiAutocomplete-popper': {
    '& .MuiPaper-root': {
      fontSize: '0.75rem'
    }
  }
};

const MasterModal = ({
  open,
  onClose,
  modalType,
  modalAction,
  selectedItem,
  formData,
  setFormData,
  onSave,
  loading
}) => {
  const { companyId, token } = useAuth();
  const { logAudit } = useAuditLog();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [errors, setErrors] = useState({});
  const [ledgerGroups, setLedgerGroups] = useState([]);
  const [ledgerGroupLoading, setLedgerGroupLoading] = useState(false);
  const [ledgerGroupInputValue, setLedgerGroupInputValue] = useState('');
  const [creatingNewGroup, setCreatingNewGroup] = useState(false);
  const [balanceSheetHeads, setBalanceSheetHeads] = useState([]);
  const [balanceSheetLoading, setBalanceSheetLoading] = useState(false);
  const [salesExecutives, setSalesExecutives] = useState([]);
  const [salesExecutiveLoading, setSalesExecutiveLoading] = useState(false);
  const [branches, setBranches] = useState([]);
  const [branchesLoading, setBranchesLoading] = useState(false);
  // Holds the raw API opening balances for edit/view — separate from formData to avoid race
  const [apiOpeningBalances, setApiOpeningBalances] = useState(null);

  useEffect(() => {
    if (open && companyId && token) {
      fetchLedgerGroups();
      fetchBalanceSheetHeads();
      fetchSalesExecutives();
      fetchBranches();
    } else if (!open) {
      // Reset branch-related state when modal closes
      setBranches([]);
      setApiOpeningBalances(null);
    }
  }, [open, companyId, token]);

  useEffect(() => {
    if (selectedItem && modalAction !== 'create') {
      // Read OpeningBalances directly from selectedItem (formData may not be updated yet)
      setApiOpeningBalances(selectedItem.OpeningBalances || []);
      // Set the input value when editing/viewing
      if (selectedItem.LedgerGroupName) {
        setLedgerGroupInputValue(selectedItem.LedgerGroupName);
      }
    } else if (modalAction === 'create') {
      setApiOpeningBalances(null);
      const defaultIsType = modalType === 'client' ? 1 : modalType === 'vendor' ? 2 : 1;
      setFormData({
        Name: '', SearchableName: '', BalanceSheetHeadId: null,
        OpeningBalances: [],
        IsActive: true,
        LedgerGroupId: null, LedgerGroupName: '', GroupCreditDays: 0, GroupCreditLimit: 0,
        IsType: defaultIsType, InvoiceTypeId: 0, SalesExecutiveId: 0, StateCodeId: null,
        AddressLine1: '', AddressLine2: '', Location: '', PinCode: '',
        DeliveryAddressLine1: '', DeliveryAddressLine2: '', DeliveryLocation: '', DeliveryPinCode: '',
        ContactNumber1: '', ContactNumber2: '', ContactNumber3: '', Email: '',
        GSTNumber: '', PanNumber: '', MSMENumber: '', CreditDays: 0, CreditLimit: 0
      });
      setLedgerGroupInputValue('');
    }
  }, [selectedItem, modalAction, modalType, setFormData]);

  // Sync input value when ledger groups are loaded and formData has a value
  useEffect(() => {
    if (ledgerGroups.length > 0 && formData.LedgerGroupId && modalAction !== 'create') {
      const matchedGroup = ledgerGroups.find(g => g.Id === formData.LedgerGroupId);
      if (matchedGroup) {
        setLedgerGroupInputValue(matchedGroup.Name);
      }
    }
  }, [ledgerGroups, formData.LedgerGroupId, modalAction]);

  const fetchLedgerGroups = async () => {
    setLedgerGroupLoading(true);
    try {
      const response = await fetchData('ClientAndVendorApi', `/LedgerGroupList/${companyId}`, token);
      setLedgerGroups(response.response_data || response || []);
    } catch (error) {
      console.error('Error fetching ledger groups:', error);
      setLedgerGroups([]);
    }
    setLedgerGroupLoading(false);
  };

  const fetchBalanceSheetHeads = async () => {
    setBalanceSheetLoading(true);
    try {
      const response = await fetchData('ClientAndVendorApi', `/BalanceSheetList/${companyId}`, token);
      setBalanceSheetHeads(response.response_data || response || []);
    } catch (error) {
      console.error('Error fetching balance sheet heads:', error);
      setBalanceSheetHeads([]);
    }
    setBalanceSheetLoading(false);
  };

  const fetchSalesExecutives = async () => {
    setSalesExecutiveLoading(true);
    try {
      const response = await fetchData('SalesExecutiveApi', `/${companyId}`, token);
      const data = response.response_data || response || [];
      setSalesExecutives(data.filter(i => i.IsActive));
    } catch (error) {
      console.error('Error fetching sales executives:', error);
      setSalesExecutives([]);
    }
    setSalesExecutiveLoading(false);
  };

  const fetchBranches = async () => {
    setBranchesLoading(true);
    try {
      const response = await fetchData('BranchApi', `/${companyId}`, token);
      const data = response.response_data || response || [];
      setBranches(Array.isArray(data) ? data : [data]);
    } catch (error) {
      console.error('Error fetching branches:', error);
      setBranches([]);
    }
    setBranchesLoading(false);
  };

  // Merge branches with API opening balances once both are ready
  useEffect(() => {
    if (branches.length === 0) return;

    const today = new Date().toISOString().split('T')[0];
    const defaultReceivable = modalType === 'client';

    if (modalAction === 'create') {
      setFormData(prev => ({
        ...prev,
        OpeningBalances: branches.map(b => ({
          BranchId: b.Id,
          BranchName: b.Name || b.BranchName || '',
          OpeningBalance: 0,
          OpeningDate: today,
          IsPayableOrReceivable: defaultReceivable
        }))
      }));
    } else if ((modalAction === 'edit' || modalAction === 'view') && apiOpeningBalances !== null) {
      // apiOpeningBalances is the saved list from the API (may be empty for old records)
      const merged = branches.map(b => {
        const found = apiOpeningBalances.find(r => r.BranchId === b.Id);
        return found
          ? { ...found, Id: found.Id || 0, BranchName: b.Name || b.BranchName || found.BranchName || '' }
          : {
            Id: 0,
            BranchId: b.Id,
            BranchName: b.Name || b.BranchName || '',
            OpeningBalance: 0,
            OpeningDate: today,
            IsPayableOrReceivable: defaultReceivable
          };
      });
      setFormData(prev => ({ ...prev, OpeningBalances: merged }));
    }
  }, [branches, modalAction, modalType, apiOpeningBalances]);

  const handleOpeningBalanceChange = (branchId, field, value) => {
    setFormData(prev => ({
      ...prev,
      OpeningBalances: (prev.OpeningBalances || []).map(row =>
        row.BranchId === branchId ? { ...row, [field]: value } : row
      )
    }));
  };

  const handleCreateLedgerGroup = async (name) => {
    if (!name?.trim()) return null;
    setCreatingNewGroup(true);
    try {
      const newGroup = {
        CompanyId: companyId, Name: name.trim(),
        CreditDays: formData.GroupCreditDays || 0,
        CreditLimit: formData.GroupCreditLimit || 0, IsActive: true
      };
      const response = await postData('ClientAndVendorApi', '/LedgerGroup', newGroup, token);
      logAudit(AUDIT_ACTION.CREATE, 'ClientAndVendorApi', response?.Id || 0, null, { Name: name.trim() });
      const createdGroup = response.response_data || response;
      await fetchLedgerGroups();
      return createdGroup;
    } catch (error) {
      console.error('Error creating ledger group:', error);
      return null;
    } finally {
      setCreatingNewGroup(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      // Auto-sync SearchableName with Name
      if (field === 'Name') {
        updated.SearchableName = value;
      }
      return updated;
    });
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleLedgerGroupChange = async (_, newValue) => {
    if (typeof newValue === 'string') {
      const createdGroup = await handleCreateLedgerGroup(newValue);
      if (createdGroup) {
        handleInputChange('LedgerGroupId', createdGroup.Id);
        handleInputChange('LedgerGroupName', createdGroup.Name);
        setLedgerGroupInputValue(createdGroup.Name);
      }
    } else if (newValue?.inputValue) {
      const createdGroup = await handleCreateLedgerGroup(newValue.inputValue);
      if (createdGroup) {
        handleInputChange('LedgerGroupId', createdGroup.Id);
        handleInputChange('LedgerGroupName', createdGroup.Name);
        setLedgerGroupInputValue(createdGroup.Name);
      }
    } else if (newValue) {
      handleInputChange('LedgerGroupId', newValue.Id);
      handleInputChange('LedgerGroupName', newValue.Name);
      setLedgerGroupInputValue(newValue.Name);
    } else {
      handleInputChange('LedgerGroupId', null);
      handleInputChange('LedgerGroupName', '');
      setLedgerGroupInputValue('');
    }
  };

  const handleBothToggle = (checked) => {
    handleInputChange('IsType', checked ? 3 : (modalType === 'client' ? 1 : 2));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!(formData.Name || formData.name)?.toString().trim()) newErrors.Name = 'Name is required';
    if (!formData.BalanceSheetHeadId) newErrors.BalanceSheetHeadId = 'Balance sheet head is required';
    if (!formData.ContactNumber1?.toString().trim()) newErrors.ContactNumber1 = 'Contact number is required';
    if (formData.ContactNumber1?.trim() && !/^\d{10}$/.test(formData.ContactNumber1)) newErrors.ContactNumber1 = 'Enter a valid 10-digit number';
    if (formData.ContactNumber2?.trim() && !/^\d{10}$/.test(formData.ContactNumber2)) newErrors.ContactNumber2 = 'Enter a valid 10-digit number';
    if (formData.ContactNumber3?.trim() && !/^\d{10}$/.test(formData.ContactNumber3)) newErrors.ContactNumber3 = 'Enter a valid 10-digit number';
    if (!formData.AddressLine1?.toString().trim()) newErrors.AddressLine1 = 'Address is required';
    if (formData.Email?.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.Email)) newErrors.Email = 'Please enter a valid email address';
    if (formData.PinCode?.trim() && !/^\d{6}$/.test(formData.PinCode)) newErrors.PinCode = 'Please enter a valid 6-digit PIN code';
    if (formData.DeliveryPinCode?.trim() && !/^\d{6}$/.test(formData.DeliveryPinCode)) newErrors.DeliveryPinCode = 'Please enter a valid 6-digit PIN code';
    if (formData.GSTNumber?.trim() && !/^\d{2}[A-Z]{5}\d{4}[A-Z]{1}[A-Z\d]{1}Z[A-Z\d]{1}$/.test(formData.GSTNumber.trim().toUpperCase())) newErrors.GSTNumber = 'Enter a valid GST number (e.g. 22AAAAA0000A1Z5)';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => { if (validateForm()) onSave(); };

  const getModalTitle = () => {
    const typeNames = { client: 'Client', vendor: 'Vendor' };
    const actionNames = { create: 'New', edit: 'Edit', view: 'View' };
    const typeName = typeNames[modalType] || 'Master';
    return {
      title: `${actionNames[modalAction] || 'New'} ${typeName}`,
      subtitle: `${modalAction === 'create' ? 'Add a new' : modalAction === 'edit' ? 'Update' : 'View'} ${typeName.toLowerCase()} details`
    };
  };

  const filterOptions = (options, params) => {
    const filtered = options.filter(opt => opt.Name?.toLowerCase().includes(params.inputValue.toLowerCase()));
    if (params.inputValue !== '' && !filtered.some(opt => opt.Name?.toLowerCase() === params.inputValue.toLowerCase())) {
      filtered.push({ inputValue: params.inputValue, Name: `Add "${params.inputValue}"` });
    }
    return filtered;
  };

  const isDisabled = modalAction === 'view';

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
          onClose();
        }
      }}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      disableEscapeKeyDown
      className="item-modal"
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : '12px'
        }
      }}
    >
      <DialogTitle sx={{ p: 0 }}>
        <div className="modal-title-container">
          <div className="modal-title-text">
            <h6>{getModalTitle().title}</h6>
          </div>
          <Button onClick={onClose} className="modal-close-btn"><Close /></Button>
        </div>
      </DialogTitle>

      <DialogContent sx={{ p: 1, pt: 0.25 }}>
        {/* Basic Information */}
        <Box sx={{ ...sectionStyle, mt: 0, mb: 0.5 }}>Basic Information</Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 0.75, mb: 0.75 }}>
          <TextField size="small" label="Name *" value={formData.Name || ''} onChange={(e) => handleInputChange('Name', e.target.value)} error={!!errors.Name} helperText={errors.Name} disabled={isDisabled} sx={fieldStyle} />
          <TextField size="small" label="Searchable Name" value={formData.SearchableName || ''} onChange={(e) => handleInputChange('SearchableName', e.target.value)} disabled={isDisabled} sx={fieldStyle} />
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 1, mb: 0.75 }}>
          <Autocomplete
            size="small"
            value={balanceSheetHeads.find(b => b.Id === formData.BalanceSheetHeadId) || null}
            onChange={(_, v) => handleInputChange('BalanceSheetHeadId', v?.Id || null)}
            options={balanceSheetHeads}
            getOptionLabel={(opt) => opt.Name || ''}
            loading={balanceSheetLoading}
            disabled={isDisabled}
            sx={fieldStyle}
            slotProps={{ popper: { sx: autocompleteListStyle } }}
            renderInput={(params) => <TextField {...params} label="Balance Sheet Head *" error={!!errors.BalanceSheetHeadId} helperText={errors.BalanceSheetHeadId} InputProps={{ ...params.InputProps, endAdornment: <>{balanceSheetLoading && <CircularProgress size={14} />}{params.InputProps.endAdornment}</> }} />}
          />
          {modalType === 'client' && (
            <Autocomplete
              size="small"
              value={salesExecutives.find(s => s.Id === formData.SalesExecutiveId) || null}
              onChange={(_, v) => handleInputChange('SalesExecutiveId', v?.Id || 0)}
              options={salesExecutives}
              getOptionLabel={(opt) => opt.Name || ''}
              loading={salesExecutiveLoading}
              disabled={isDisabled}
              sx={fieldStyle}
              slotProps={{ popper: { sx: autocompleteListStyle } }}
              renderInput={(params) => (
                <TextField {...params} label="Sales Executive" InputProps={{ ...params.InputProps, endAdornment: <>{salesExecutiveLoading && <CircularProgress size={14} />}{params.InputProps.endAdornment}</> }} />
              )}
            />
          )}
        </Box>

        {/* Branch-based Opening Balances - moved to after Tax & Credit */}

        {/* Ledger Group */}
        <Box sx={sectionStyle}>Ledger Group</Box>
        <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 1, mb: 0.75, alignItems: isMobile ? 'stretch' : 'center' }}>
          <Autocomplete
            size="small"
            value={ledgerGroups.find(g => g.Id === formData.LedgerGroupId) || null}
            onChange={handleLedgerGroupChange}
            inputValue={ledgerGroupInputValue}
            onInputChange={(_, v, r) => { if (r !== 'reset') { setLedgerGroupInputValue(v); } }}
            onBlur={() => {
              const sel = ledgerGroups.find(g => g.Id === formData.LedgerGroupId);
              if (sel) { setLedgerGroupInputValue(sel.Name || ''); handleInputChange('LedgerGroupName', sel.Name || ''); }
              else if (ledgerGroupInputValue) handleInputChange('LedgerGroupName', ledgerGroupInputValue);
            }}
            filterOptions={filterOptions}
            options={ledgerGroups}
            getOptionLabel={(opt) => typeof opt === 'string' ? opt : opt.inputValue || opt.Name || ''}
            renderOption={(props, opt) => <li {...props}>{opt.inputValue ? <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#1e3a8a' }}><Add fontSize="small" /> {opt.Name}</span> : opt.Name}</li>}
            freeSolo selectOnFocus handleHomeEndKeys
            loading={ledgerGroupLoading || creatingNewGroup}
            disabled={isDisabled}
            sx={{ flex: 1, ...fieldStyle }}
            slotProps={{
              popper: {
                sx: autocompleteListStyle
              }
            }}
            renderInput={(params) => <TextField {...params} label="Ledger Group (Select or Create)" InputProps={{ ...params.InputProps, endAdornment: <>{(ledgerGroupLoading || creatingNewGroup) && <CircularProgress size={14} />}{params.InputProps.endAdornment}</> }} />}
          />
          {!isDisabled && (
            <FormControlLabel control={<Switch size="small" checked={formData.IsType === 3} onChange={(e) => handleBothToggle(e.target.checked)} />} label={`Also mark as ${modalType === 'client' ? 'Vendor' : 'Client'} (Both)`} sx={{ whiteSpace: isMobile ? 'normal' : 'nowrap', '& .MuiFormControlLabel-label': { fontSize: '0.7rem' } }} />
          )}
        </Box>

        {/* Type section removed as it's now inline */}

        {/* Contact Information */}
        <Box sx={sectionStyle}>Contact Information</Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: 1, mb: 1 }}>
          {/* <TextField size="small" label="Contact Number 1 *" value={formData.ContactNumber1 || ''} onChange={(e) => handleInputChange('ContactNumber1', e.target.value)} error={!!errors.ContactNumber1} helperText={errors.ContactNumber1} disabled={isDisabled} sx={fieldStyle} />
          <TextField size="small" label="Contact Number 2" value={formData.ContactNumber2 || ''} onChange={(e) => handleInputChange('ContactNumber2', e.target.value)} disabled={isDisabled} sx={fieldStyle} />
          <TextField size="small" label="Contact Number 3" value={formData.ContactNumber3 || ''} onChange={(e) => handleInputChange('ContactNumber3', e.target.value)} disabled={isDisabled} sx={fieldStyle} /> */}
          <TextField
            size="small"
            label="Contact Number 1 *"
            value={formData.ContactNumber1 || ''}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '');
              if (value.length <= 10) {
                handleInputChange('ContactNumber1', value);
              }
            }}
            error={!!errors.ContactNumber1}
            helperText={errors.ContactNumber1}
            disabled={isDisabled}
            inputProps={{
              maxLength: 10,
              inputMode: 'numeric'
            }}
            sx={fieldStyle}
          />

          <TextField
            size="small"
            label="Contact Number 2"
            value={formData.ContactNumber2 || ''}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '');
              if (value.length <= 10) {
                handleInputChange('ContactNumber2', value);
              }
            }}
            error={!!errors.ContactNumber2}
            helperText={errors.ContactNumber2}
            disabled={isDisabled}
            inputProps={{
              maxLength: 10,
              inputMode: 'numeric'
            }}
            sx={fieldStyle}
          />

          <TextField
            size="small"
            label="Contact Number 3"
            value={formData.ContactNumber3 || ''}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '');
              if (value.length <= 10) {
                handleInputChange('ContactNumber3', value);
              }
            }}
            error={!!errors.ContactNumber3}
            helperText={errors.ContactNumber3}
            disabled={isDisabled}
            inputProps={{
              maxLength: 10,
              inputMode: 'numeric'
            }}
            sx={fieldStyle}
          />
        </Box>
        {/* <TextField size="small" fullWidth label="Email" type="email" value={formData.Email || ''} onChange={(e) => handleInputChange('Email', e.target.value)} disabled={isDisabled} sx={{ mb: 1, ...fieldStyle }} /> */}

        <TextField
          size="small"
          fullWidth
          label="Email"
          type="email"
          value={formData.Email || ''}
          onChange={(e) => {
            // Strip spaces — spaces are never valid in an email address
            const value = e.target.value.replace(/\s/g, '');
            handleInputChange('Email', value);
            // Clear error as soon as the field becomes valid while typing
            if (errors.Email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
              setErrors(prev => ({ ...prev, Email: '' }));
            }
          }}
          onBlur={() => {
            const email = formData.Email?.trim() || '';
            if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
              setErrors(prev => ({ ...prev, Email: 'Please enter a valid email address' }));
            } else {
              setErrors(prev => ({ ...prev, Email: '' }));
            }
          }}
          disabled={isDisabled}
          error={!!errors.Email}
          helperText={errors.Email || ''}
          inputProps={{ maxLength: 100 }}
          sx={{ mb: 1, ...fieldStyle }}
        />

        {/* Address */}
        <Box sx={sectionStyle}>Address</Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '2.5fr 1fr', gap: 1, mb: 1 }}>
          <TextField size="small" label="Address Line 1 *" value={formData.AddressLine1 || ''} onChange={(e) => handleInputChange('AddressLine1', e.target.value)} error={!!errors.AddressLine1} helperText={errors.AddressLine1} disabled={isDisabled} sx={fieldStyle} />
          <TextField size="small" label="Location" value={formData.Location || ''} onChange={(e) => handleInputChange('Location', e.target.value)} disabled={isDisabled} sx={fieldStyle} />
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '2.5fr 1fr', gap: 1, mb: 1 }}>
          <TextField size="small" label="Address Line 2" value={formData.AddressLine2 || ''} onChange={(e) => handleInputChange('AddressLine2', e.target.value)} disabled={isDisabled} sx={fieldStyle} />
          {/* <TextField size="small" label="Pin Code" value={formData.PinCode || ''} onChange={(e) => handleInputChange('PinCode', e.target.value)} disabled={isDisabled} sx={fieldStyle} /> */}
          <TextField
            size="small"
            label="Pin Code"
            value={formData.PinCode || ''}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '');

              if (value.length <= 6) {
                handleInputChange('PinCode', value);
              }
            }}
            disabled={isDisabled}
            inputProps={{
              maxLength: 6,
              inputMode: 'numeric'
            }}
            error={!!errors.PinCode || (!!formData.PinCode && formData.PinCode.length !== 6)}
            helperText={
              errors.PinCode ||
              (formData.PinCode && formData.PinCode.length !== 6
                ? 'Please enter a valid 6-digit PIN code.'
                : '')
            }
            sx={fieldStyle}
          />
        </Box>

        {/* Delivery Address */}
        <Box sx={sectionStyle}>Delivery Address</Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '2.5fr 1fr', gap: 1, mb: 1 }}>
          <TextField size="small" label="Delivery Address Line 1" value={formData.DeliveryAddressLine1 || ''} onChange={(e) => handleInputChange('DeliveryAddressLine1', e.target.value)} disabled={isDisabled} sx={fieldStyle} />
          <TextField size="small" label="Delivery Location" value={formData.DeliveryLocation || ''} onChange={(e) => handleInputChange('DeliveryLocation', e.target.value)} disabled={isDisabled} sx={fieldStyle} />
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '2.5fr 1fr', gap: 1, mb: 1 }}>
          <TextField size="small" label="Delivery Address Line 2" value={formData.DeliveryAddressLine2 || ''} onChange={(e) => handleInputChange('DeliveryAddressLine2', e.target.value)} disabled={isDisabled} sx={fieldStyle} />
          {/* <TextField size="small" label="Delivery Pin Code" value={formData.DeliveryPinCode || ''} onChange={(e) => handleInputChange('DeliveryPinCode', e.target.value)} disabled={isDisabled} sx={fieldStyle} /> */}
          <TextField
            size="small"
            label="Delivery Pin Code"
            value={formData.DeliveryPinCode || ''}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '');

              if (value.length <= 6) {
                handleInputChange('DeliveryPinCode', value);
              }
            }}
            disabled={isDisabled}
            inputProps={{
              maxLength: 6,
              inputMode: 'numeric'
            }}
            error={
              !!errors.DeliveryPinCode ||
              (!!formData.DeliveryPinCode &&
              formData.DeliveryPinCode.length !== 6)
            }
            helperText={
              errors.DeliveryPinCode ||
              (formData.DeliveryPinCode &&
                formData.DeliveryPinCode.length !== 6
                ? 'Please enter a valid 6-digit PIN code.'
                : '')
            }
            sx={fieldStyle}
          />
        </Box>

        {/* Tax & Credit */}
        <Box sx={sectionStyle}>Tax & Credit Information</Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr 1fr', gap: 1, mb: 1 }}>
          <TextField size="small" label="GST Number" value={formData.GSTNumber || ''} onChange={(e) => handleInputChange('GSTNumber', e.target.value.toUpperCase())} disabled={isDisabled} error={!!errors.GSTNumber || (!!formData.GSTNumber && !/^\d{2}[A-Z]{5}\d{4}[A-Z]{1}[A-Z\d]{1}Z[A-Z\d]{1}$/.test(formData.GSTNumber.toUpperCase()))} helperText={errors.GSTNumber || (formData.GSTNumber && !/^\d{2}[A-Z]{5}\d{4}[A-Z]{1}[A-Z\d]{1}Z[A-Z\d]{1}$/.test(formData.GSTNumber.toUpperCase()) ? 'Enter a valid GST number (e.g. 22AAAAA0000A1Z5)' : '')} inputProps={{ maxLength: 15 }} sx={fieldStyle} />
          <TextField size="small" label="Credit Limit (₹)" type="number" value={formData.CreditLimit ?? 0} onChange={(e) => handleInputChange('CreditLimit', parseFloat(e.target.value) || 0)} disabled={isDisabled} inputProps={{ min: 0 }} sx={fieldStyle} />
          <TextField size="small" label="Credit Days" type="number" value={formData.CreditDays ?? 0} onChange={(e) => handleInputChange('CreditDays', parseInt(e.target.value) || 0)} disabled={isDisabled} inputProps={{ min: 0 }} sx={fieldStyle} />
        </Box>

        {/* Branch-based Opening Balances */}
        <Box sx={{ ...sectionStyle, mb: 0.5 }}>Opening Balances (Branch-wise)</Box>
        <Box sx={{ mb: 0.75 }}>
          {branchesLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
              <CircularProgress size={14} />
              <span style={{ fontSize: '0.72rem', color: '#64748b' }}>Loading branches...</span>
            </Box>
          ) : (
            <Box sx={{ border: '1px solid #e5e7eb', borderRadius: '6px', overflow: 'hidden' }}>
              <Table size="small" sx={{ tableLayout: 'fixed' }}>
                <TableHead>
                  <TableRow sx={{ background: '#f1f5f9' }}>
                    <TableCell sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#1e3a8a', py: 0.5, px: 1, width: '28%' }}>Branch</TableCell>
                    <TableCell sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#1e3a8a', py: 0.5, px: 1, width: '28%' }}>Opening Balance (₹)</TableCell>
                    <TableCell sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#1e3a8a', py: 0.5, px: 1, width: '26%' }}>Opening Date</TableCell>
                    <TableCell sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#1e3a8a', py: 0.5, px: 1, width: '18%', textAlign: 'center' }}>
                      {modalType === 'client' ? 'Receivable' : 'Payable'}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(formData.OpeningBalances || []).map((row, idx) => (
                    <TableRow key={row.BranchId} sx={{ background: idx % 2 === 0 ? '#fff' : '#f8f9fa' }}>
                      <TableCell sx={{ fontSize: '0.72rem', py: 0.4, px: 1, color: '#374151', fontWeight: 500 }}>
                        {row.BranchName}
                      </TableCell>
                      <TableCell sx={{ py: 0.4, px: 1 }}>
                        <TextField
                          size="small"
                          type="number"
                          value={row.OpeningBalance ?? 0}
                          onChange={(e) => handleOpeningBalanceChange(row.BranchId, 'OpeningBalance', parseFloat(e.target.value) || 0)}
                          disabled={isDisabled}
                          inputProps={{ min: 0, style: { padding: '3px 6px', fontSize: '0.72rem' } }}
                          sx={{ '& .MuiOutlinedInput-root': { minHeight: '26px' }, width: '100%' }}
                        />
                      </TableCell>
                      <TableCell sx={{ py: 0.4, px: 1 }}>
                        <TextField
                          size="small"
                          type="date"
                          value={row.OpeningDate?.split('T')[0] || ''}
                          onChange={(e) => handleOpeningBalanceChange(row.BranchId, 'OpeningDate', e.target.value)}
                          disabled={isDisabled}
                          InputLabelProps={{ shrink: true }}
                          inputProps={{ style: { padding: '3px 6px', fontSize: '0.72rem' } }}
                          sx={{ '& .MuiOutlinedInput-root': { minHeight: '26px' }, width: '100%' }}
                        />
                      </TableCell>
                      <TableCell sx={{ py: 0.4, px: 1, textAlign: 'center' }}>
                        <Switch
                          size="small"
                          checked={row.IsPayableOrReceivable ?? (modalType === 'client')}
                          onChange={(e) => handleOpeningBalanceChange(row.BranchId, 'IsPayableOrReceivable', e.target.checked)}
                          disabled={isDisabled}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                  {(formData.OpeningBalances || []).length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} sx={{ fontSize: '0.72rem', color: '#9ca3af', textAlign: 'center', py: 1 }}>
                        No branches available
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 1, py: 0.75 }}>
        <Button onClick={onClose} variant="outlined" size="small" sx={{ fontSize: '0.7rem', padding: '4px 12px' }}>Cancel</Button>
        {!isDisabled && (
          <Button
            onClick={handleSave}
            variant="contained"
            size="small"
            disabled={loading || creatingNewGroup}
            sx={{ fontSize: '0.7rem', padding: '4px 12px', minWidth: '100px' }}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {loading ? 'Saving...' : modalAction === 'create' ? `Add ${modalType?.charAt(0).toUpperCase() + modalType?.slice(1)}` : 'Update'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default MasterModal;


